/**
 * MODULE: _shared/frontend-shell/src/components/templates/GlobalEmailTemplateCreateHost.tsx
 * PURPOSE: Single app-level EmailTemplateCreateModal for assistant dock, overlay, Sales Hub, and context chips.
 * RECENT: 2026-06-06 [2.2.1328] — triggerEmailTemplateLearningRefresh + notifyEmailTemplateCreated on save when coachInAssistant.
 */

import { useCallback, useEffect, useState } from 'react';
import type {
  EmailTemplateCreateOpenOptions,
  EmailTemplateCreatePrefill,
} from '@shared/lib/frontend/utils/emailTemplateCreateBridge';
import {
  notifyEmailTemplateCreated,
  subscribeOpenEmailTemplateCreate,
  triggerEmailTemplateLearningRefresh,
} from '@shared/lib/frontend/utils/emailTemplateCreateBridge';
import { useToast } from '@design-system/components';
import EmailTemplateCreateModal from './EmailTemplateCreateModal';

export function GlobalEmailTemplateCreateHost() {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<EmailTemplateCreatePrefill | undefined>();
  const [successHint, setSuccessHint] = useState<EmailTemplateCreateOpenOptions['successHint']>();
  const [coachInAssistant, setCoachInAssistant] = useState(false);
  const { showToast } = useToast();

  useEffect(
    () =>
      subscribeOpenEmailTemplateCreate((options) => {
        setPrefill(options.prefill);
        setSuccessHint(options.successHint);
        setCoachInAssistant(Boolean(options.coachInAssistant));
        setOpen(true);
      }),
    []
  );

  const handleSuccess = useCallback(
    (result?: { title?: string }) => {
    setOpen(false);
    setPrefill(undefined);
    triggerEmailTemplateLearningRefresh();
    notifyEmailTemplateCreated({
      successHint,
      mode: 'create',
      templateTitle: result?.title,
      coachInAssistant,
    });
    setSuccessHint(undefined);
    setCoachInAssistant(false);
    showToast({
      message: 'Email template saved. Assistant chips will update with your top templates.',
      variant: 'success',
    });
  },
    [showToast, successHint, coachInAssistant]
  );

  return (
    <EmailTemplateCreateModal
      isOpen={open}
      onClose={() => {
        setOpen(false);
        setPrefill(undefined);
        setSuccessHint(undefined);
        setCoachInAssistant(false);
      }}
      onSuccess={handleSuccess}
      initialCategory="outreach"
      initialPrefill={prefill}
    />
  );
}
